import { cn } from '@gozoki/ui/lib/utils'
import type { ReactNode } from 'react'
import { ChevronRightRoundIcon } from '../icons/LayerIcon'
import { TradeUpIcon } from '../icons/dashboard-icons'

// Surface commune des cartes du dashboard (Figma 10715:55849) : rayon 24, padding 17, gap 16.
// Le fond et la bordure varient d'une carte à l'autre et se passent par `className`.
export function DashboardCard({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col gap-[16px] overflow-hidden rounded-[24px] border p-[17px]',
        className,
      )}
      {...props}
    />
  )
}

// En-tête : pastille d'icône, titre Bricolage 22, méta optionnelle à droite, chevron d'ouverture.
export function CardTitle({
  icon,
  iconBoxClassName,
  title,
  titleClassName,
  meta,
  metaClassName,
  onOpen,
  openLabel,
}: {
  icon: ReactNode
  iconBoxClassName?: string
  title: string
  titleClassName?: string
  meta?: ReactNode
  metaClassName?: string
  onOpen?: () => void
  openLabel?: string
}) {
  return (
    <div className="flex items-center gap-[8px]">
      <div className="flex min-w-0 flex-1 items-center gap-[12px]">
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-[12px] p-[8px]',
            iconBoxClassName,
          )}
        >
          {icon}
        </span>
        <span
          className={cn(
            'min-w-0 flex-1 font-brand text-[22px] leading-[26px] font-semibold whitespace-nowrap',
            titleClassName,
          )}
        >
          {title}
        </span>
      </div>
      {meta ? (
        <span
          className={cn(
            'shrink-0 font-brand text-[18px] leading-[20px] font-medium whitespace-nowrap',
            metaClassName,
          )}
        >
          {meta}
        </span>
      ) : null}
      {openLabel ? (
        <button
          type="button"
          aria-label={openLabel}
          onClick={onOpen}
          // p/-m : cible tactile ≥44px à l'écran (24px ×1.487) sans bouger le glyphe (kiosk PCAP).
          className={cn('shrink-0 cursor-pointer p-[6px] -m-[6px]', titleClassName)}
        >
          <ChevronRightRoundIcon />
        </button>
      ) : null}
    </div>
  )
}

// État vide des cartes dont la source de données n'existe pas encore. Le ton par défaut convient
// aux fonds clairs ; les cartes sombres ou colorées passent leur propre couleur via `className`.
export function EmptyState({ children, className }: { children: string; className?: string }) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center">
      <span
        className={cn(
          'text-center font-sans text-[14px] leading-[16px] font-medium text-[#7C867D]',
          className,
        )}
      >
        {children}
      </span>
    </div>
  )
}

export function CardDivider({ className }: { className?: string }) {
  return <span className={cn('h-px w-full shrink-0 bg-[#0000000F]', className)} />
}

// Badge de tendance : flèche + variation + période de comparaison.
export function TrendBadge({
  value,
  className,
  size = 32,
}: {
  value: number | null
  className?: string
  size?: number
}) {
  if (value === null || !Number.isFinite(value)) {
    return <span className={cn('font-sans text-[14px] font-semibold', className)}>—</span>
  }
  return (
    <span className={cn('flex shrink-0 items-center gap-[6px]', className)}>
      <span
        className={cn('flex items-center justify-center', value < 0 && 'rotate-x-180')}
        style={{ width: size, height: size }}
      >
        <TradeUpIcon />
      </span>
      <span className="flex flex-col items-start">
        <span className="font-sans text-[14px] leading-[16px] font-semibold">
          {value > 0 ? '+' : ''}
          {value.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}%
        </span>
        <span className="font-sans text-[12px] leading-[14px] font-semibold">vs S-1</span>
      </span>
    </span>
  )
}
